import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { GlobeIcon, FileTextIcon, ClipboardCheckIcon, UsersIcon, TruckIcon, TrendingUpIcon } from 'lucide-react';
import { useSEO } from '../hooks/useSEO';
export function Services() {
  useSEO({
    title: 'Our Services',
    description: 'Global product sourcing, procurement coordination, tender support, supplier verification, contract liaison, logistics coordination, and procurement advisory services for institutional and commercial buyers.',
    path: '/services'
  });
  const services = [{
    icon: GlobeIcon,
    title: 'Global Product Sourcing & Supplier Identification',
    description: 'We leverage our extensive network of verified manufacturers, exporters, and distributors to identify suppliers capable of meeting large-volume procurement demands across all major sectors.',
    deliverables: ['Supplier database search and shortlisting', 'Factory capability assessment', 'Production capacity verification', 'Quality management system review', 'Export experience evaluation', 'Financial stability checks']
  }, {
    icon: FileTextIcon,
    title: 'Procurement Coordination & Tender Support',
    description: 'End-to-end tender coordination services for institutional and commercial procurement programmes, ensuring compliance with multilateral and government procurement procedures.',
    deliverables: ['Tender documentation preparation', 'Technical specification review', 'Commercial proposal development', 'Compliance verification', 'Submission management', 'Post-tender clarifications']
  }, {
    icon: ClipboardCheckIcon,
    title: 'Supplier Price & Specification Evaluation',
    description: 'Comprehensive technical and commercial evaluation of supplier proposals against tender requirements, ensuring optimal value and compliance with procurement standards.',
    deliverables: ['Technical specification analysis', 'Pricing competitiveness review', 'Terms and conditions evaluation', 'Compliance gap analysis', 'Value engineering recommendations', 'Comparative evaluation reports']
  }, {
    icon: UsersIcon,
    title: 'Contract Allocation Assistance & Project Liaison',
    description: 'We facilitate communication between suppliers and procurement entities, coordinate contract negotiations, and provide ongoing project liaison throughout the procurement lifecycle.',
    deliverables: ['Buyer-supplier introductions', 'Contract negotiation support', 'Terms and conditions mediation', 'Payment terms structuring', 'Ongoing project coordination', 'Issue resolution and escalation']
  }, {
    icon: TruckIcon,
    title: 'Logistics Coordination & International Trade Support',
    description: 'Comprehensive logistics management for cross-border procurement, including export documentation, freight coordination, customs clearance, and in-country delivery.',
    deliverables: ['Export documentation preparation', 'Freight forwarder coordination', 'Customs clearance management', 'Incoterms advisory', 'Insurance arrangement', 'In-country delivery coordination']
  }, {
    icon: TrendingUpIcon,
    title: 'Market Research & Procurement Advisory',
    description: 'Strategic advisory services on African procurement markets, regulatory compliance, trade finance options, and market entry strategies for international suppliers.',
    deliverables: ['Market opportunity analysis', 'Regulatory compliance guidance', 'Trade finance structuring', 'Market entry strategy development', 'Competitive landscape assessment', 'Risk mitigation planning']
  }];
  return <div className="pt-24" data-id="element-601">
      {/* Hero Band */}
      <section className="py-20 px-6 bg-navy-light" data-id="element-602">
        <div className="max-w-4xl mx-auto text-center" data-id="element-603">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} data-id="element-604">
            <div className="flex items-center justify-center gap-3 mb-6" data-id="element-605">
              <div className="w-12 h-px bg-gold" data-id="element-606"></div>
              <span className="text-gold text-xs font-semibold tracking-widest uppercase" data-id="element-607">
                What We Do
              </span>
              <div className="w-12 h-px bg-gold" data-id="element-608"></div>
            </div>
            <h1 className="text-5xl lg:text-6xl font-semibold text-white mb-6" data-id="element-609">
              Our Services
            </h1>
            <p className="text-slate-400 text-lg leading-relaxed" data-id="element-610">
              Comprehensive procurement solutions from supplier identification
              through to final delivery
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Detail */}
      {services.map((service, i) => <section key={service.title} className={`py-20 px-6 ${i % 2 === 0 ? 'bg-navy-dark' : 'bg-navy-light'}`} data-id="element-611">
          <div className="max-w-7xl mx-auto" data-id="element-612">
            <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="grid lg:grid-cols-2 gap-12 items-center" data-id="element-613">
              <div className={i % 2 === 0 ? '' : 'lg:order-2'} data-id="element-614">
                <div className="w-16 h-16 bg-gold/10 border border-gold/30 rounded-lg flex items-center justify-center mb-6" data-id="element-615">
                  <service.icon size={32} className="text-gold" data-id="element-616" />
                </div>
                <h2 className="text-3xl lg:text-4xl font-semibold text-white mb-6" data-id="element-617">
                  {service.title}
                </h2>
                <p className="text-slate-400 text-lg leading-relaxed mb-6" data-id="element-618">
                  {service.description}
                </p>
              </div>

              <div className={i % 2 === 0 ? '' : 'lg:order-1'} data-id="element-619">
                <div className="bg-navy-light border border-slate-800 p-8 rounded-lg" data-id="element-620">
                  <h3 className="text-lg font-semibold text-white mb-4" data-id="element-621">
                    Key Deliverables
                  </h3>
                  <ul className="space-y-3" data-id="element-622">
                    {service.deliverables.map(item => <li key={item} className="flex items-start gap-3" data-id="element-623">
                        <div className="w-1.5 h-1.5 bg-gold rounded-full mt-2 flex-shrink-0" data-id="element-624"></div>
                        <span className="text-slate-400" data-id="element-625">{item}</span>
                      </li>)}
                  </ul>
                </div>
              </div>
            </motion.div>
          </div>
        </section>)}

      {/* CTA */}
      <section className="py-20 px-6 bg-navy-dark" data-id="element-626">
        <div className="max-w-4xl mx-auto text-center" data-id="element-627">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-628">
            <h2 className="text-3xl lg:text-4xl font-semibold text-white mb-6" data-id="element-629">
              Need Procurement Support?
            </h2>
            <p className="text-slate-400 text-lg mb-8" data-id="element-630">
              Register as a supplier or contact our team to discuss your
              specific requirements
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-631">
              <Link to="/supplier-registration" className="bg-gold hover:bg-gold-dark text-navy-dark px-8 py-3 rounded-md font-semibold text-sm transition-colors" data-id="element-632">
                Register as a Supplier
              </Link>
              <Link to="/contact" className="border-2 border-slate-700 hover:border-gold text-white px-8 py-3 rounded-md font-semibold text-sm transition-colors" data-id="element-633">
                Contact Us
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>;
}